import {
  AnimationBuilder,
  AnimationFactory,
  AnimationPlayer,
  animate,
  style
} from '@angular/animations';
import { Injectable } from '@angular/core';
import { Observable, Subject, take, tap } from 'rxjs';

import { ViewService } from 'tangoe-design-system';

@Injectable({
  providedIn: 'root'
})
export class LayoutAnimationService {
  private fade_in_factory: AnimationFactory;
  private fade_out_factory: AnimationFactory;

  private current_observable: Observable<string> | undefined = undefined;

  constructor(private builder: AnimationBuilder, private view_svc: ViewService) {
    this.fade_in_factory = builder.build([
      style({ opacity: 0 }),
      animate('0.3s ease-in', style({ opacity: 1 }))
    ]);
    this.fade_out_factory = builder.build([
      style({ opacity: 1 }),
      animate('0.2s ease-out', style({ opacity: 0 }))
    ]);
  }

  isAnimating(): boolean {
    return this.current_observable !== undefined;
  }

  onCurrentAnimationDone(callback: () => void): void {
    if (!this.current_observable) {
      callback();
      return;
    }

    this.current_observable.subscribe({ complete: callback });
  }

  layoutFadeOut(): Observable<'start' | 'done'> {
    return this.setCurrentObservable<'start' | 'done'>(
      this.animateFadeOut(() => this.view_svc.resetScrollPosition())
    );
  }

  layoutFadeIn(): Observable<'start' | 'done'> {
    return this.setCurrentObservable<'start' | 'done'>(
      this.animateFadeIn(() => this.view_svc.resetScrollPosition())
    );
  }

  LayoutFadeOutAndIn(): Observable<'start' | 'fadeOutDone' | 'fadeInStart' | 'done'> {
    const subject: Subject<'start' | 'fadeOutDone' | 'fadeInStart' | 'done'> = new Subject();
    this.animateFadeOut(() => this.view_svc.resetScrollPosition()).subscribe({
      next: (event: 'start' | 'done') => subject.next(event === 'start' ? 'start' : 'fadeOutDone'),
      complete: () =>
        this.animateFadeIn().subscribe((event: 'start' | 'done') => {
          if (event === 'start') subject.next('fadeInStart');
          else {
            subject.next('done');
            subject.complete();
          }
        })
    });

    return this.setCurrentObservable<'start' | 'fadeOutDone' | 'fadeInStart' | 'done'>(subject, 4);
  }

  private animateFadeIn(start?: () => void, done?: () => void): Observable<'start' | 'done'> {
    const player: AnimationPlayer = this.fade_in_factory.create(this.view_svc.scrollableElement());
    return this.animate(player, start, done);
  }

  private animateFadeOut(start?: () => void, done?: () => void): Observable<'start' | 'done'> {
    const player: AnimationPlayer = this.fade_out_factory.create(this.view_svc.scrollableElement());
    return this.animate(player, start, done);
  }

  private animate(
    player: AnimationPlayer,
    start?: () => void,
    done?: () => void
  ): Observable<'start' | 'done'> {
    const subject: Subject<'start' | 'done'> = new Subject();

    player.onStart(() => {
      start?.();
      subject.next('start');
    });

    player.onDone(() => {
      done?.();
      subject.next('done');
      subject.complete();
      player.destroy();
    });

    player.play();

    return subject;
  }

  private setCurrentObservable<T extends string>(
    observable: Observable<T>,
    take_count: number = 2
  ): Observable<T> {
    this.current_observable = observable;

    return observable.pipe(
      take(take_count),
      tap((event: T) => {
        if (event === 'done') this.current_observable = undefined;
      })
    );
  }
}
